import { useState, useEffect } from 'react';
import { useApolloClient, useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import useEmailPinAuth from '../../../../components/auth/hooks/useEmailPinAuth';
import marketingAccountLinkMutation from '../mutations/marketing-account-link';
import { loginSuccess } from '../../../../actions/auth-actions';
import { MeWithRoles } from '../../../../graphql/queries/account';
import {
  accountAddRoles as accountAddRolesMutation,
  clientStoreSet as clientStoreSetMutation,
} from '../../../../graphql/mutations/account';
import ACCOUNT_ROLES from '../../../../constants/account-roles';
import { isProAccount } from '../../../../util/account';

const useMarketingAccountOnboarding = ({ emailAddress }) => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { onEmailAuthenticationRequest, onLoginByEmail } = useEmailPinAuth();
  const [marketingAccountLink] = useMutation(marketingAccountLinkMutation);
  const [accountAddRoles] = useMutation(accountAddRolesMutation);
  const [clientStoreSet] = useMutation(clientStoreSetMutation);

  const handleEmailAuthenticationRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      await onEmailAuthenticationRequest({ emailAddress });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailAddress) {
      handleEmailAuthenticationRequest();
    }
  }, [emailAddress]);

  const onPinVerify = async ({ code }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await onLoginByEmail({ emailAddress, code });
      if (result?.data?.accountLoginByEmail?.success) {
        dispatch(loginSuccess());
      }
      return result;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const onPinCodeResend = async () => {
    await handleEmailAuthenticationRequest();
  };

  const onMarketingAccountLink = async marketingAccountId => {
    setLoading(true);
    setError(null);
    try {
      await marketingAccountLink({
        variables: {
          input: {
            marketing_account_id: marketingAccountId,
          },
        },
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // The shutterbug matches page is gated by RequireRole([SHUTTERBUG]) and the
  // email PIN login creates role-less accounts, so grant the role before
  // redirecting there. Mirrors useLoginAuth.onAttachRolesToAccount.
  const onShutterbugRoleEnsure = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { me } = {} } = await client.query({
        query: MeWithRoles,
        fetchPolicy: 'network-only',
      });

      if (!me || isProAccount(me)) {
        return;
      }

      const roles = (me.roles ?? []).map(role => role.toLowerCase());
      if (roles.includes(ACCOUNT_ROLES.SHUTTERBUG)) {
        return;
      }

      // Fire and forget, the timestamp is informational only.
      clientStoreSet({
        variables: {
          input: {
            account_id: me.id,
            key: 'shutterbug_signup_timestamp',
            data: moment().valueOf().toString(),
          },
        },
      });

      await accountAddRoles({
        variables: {
          input: {
            account_id: me.id,
            // ACCOUNT_ROLES values are lowercase, the API expects uppercase.
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      });
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    onPinVerify,
    onPinCodeResend,
    onMarketingAccountLink,
    onShutterbugRoleEnsure,
  };
};

export default useMarketingAccountOnboarding;
